import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Domain = { id: string; domain_code: string; title: string; sort_order: number };
type Item = {
  id: string;
  domain_id: string;
  item_code: string;
  title: string;
  readiness_level: string;
  self_notes?: string | null;
};
type Evidence = {
  id: string;
  item_id: string;
  evidence_type: string;
  title: string;
  reference_text?: string | null;
  url?: string | null;
};
type SipAction = {
  id: string;
  linked_item_id?: string | null;
  action_text: string;
  due_on?: string | null;
  status: string;
};
type Sip = {
  id: string;
  sip_no: string;
  title: string;
  summary: string;
  status: string;
  record_version: number;
  actions: SipAction[];
};
type Cycle = {
  id: string;
  cycle_no: string;
  title: string;
  summary?: string | null;
  status: string;
  started_on: string;
  closed_on?: string | null;
  record_version: number;
  session_name?: string;
  session_code?: string;
  framework_code?: string;
  framework_name?: string;
  version_label?: string;
  domains: Domain[];
  items: Item[];
  evidence: Evidence[];
  sips: Sip[];
};

export default function EqamsDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.eqams.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-eqams-cycle', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Cycle>(`/education/eqams/cycles/${id}`)).data,
  });
  const row = query.data;
  const editable = row?.status === 'draft' || row?.status === 'in_progress';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-eqams-cycle', id] });
    queryClient.invalidateQueries({ queryKey: ['education-eqams-cycles'] });
  };

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/eqams/cycles/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('eqamsStatusUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateItem = useMutation({
    mutationFn: (values: { itemId: string; readiness_level: string; self_notes?: string }) =>
      api.post(`/education/eqams/items/${values.itemId}`, {
        readiness_level: values.readiness_level,
        self_notes: values.self_notes,
      }),
    onSuccess: () => {
      message.success(t('eqamsItemUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addEvidence = useMutation({
    mutationFn: (values: {
      item_id: string;
      evidence_type: string;
      title: string;
      reference_text?: string;
      url?: string;
    }) =>
      api.post(`/education/eqams/items/${values.item_id}/evidence`, {
        evidence_type: values.evidence_type,
        title: values.title,
        reference_text: values.reference_text,
        url: values.url,
      }),
    onSuccess: () => {
      message.success(t('eqamsEvidenceAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const createSip = useMutation({
    mutationFn: (values: { title: string; summary: string }) =>
      api.post(`/education/eqams/cycles/${id}/sips`, values),
    onSuccess: () => {
      message.success(t('eqamsSipCreated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const sipStatus = useMutation({
    mutationFn: ({ sipId, status, record_version }: { sipId: string; status: string; record_version: number }) =>
      api.post(`/education/eqams/sips/${sipId}/status`, { status, record_version }),
    onSuccess: () => {
      message.success(t('eqamsSipUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addAction = useMutation({
    mutationFn: (values: {
      sip_id: string;
      action_text: string;
      due_on?: dayjs.Dayjs;
      linked_item_id?: string;
    }) =>
      api.post(`/education/eqams/sips/${values.sip_id}/actions`, {
        action_text: values.action_text,
        due_on: values.due_on ? values.due_on.format('YYYY-MM-DD') : null,
        linked_item_id: values.linked_item_id || null,
      }),
    onSuccess: () => {
      message.success(t('eqamsSipActionAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const actionStatus = useMutation({
    mutationFn: ({ actionId, status }: { actionId: string; status: string }) =>
      api.post(`/education/eqams/sip-actions/${actionId}/status`, { status }),
    onSuccess: () => {
      message.success(t('eqamsSipActionUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const readinessOptions = [
    { value: 'not_started', label: t('readinessNotStarted') },
    { value: 'emerging', label: t('readinessEmerging') },
    { value: 'developing', label: t('readinessDeveloping') },
    { value: 'established', label: t('readinessEstablished') },
    { value: 'not_applicable', label: t('readinessNotApplicable') },
  ];

  return (
    <EducationPageChrome
      title={row?.cycle_no || t('eqams')}
      description={t('eqamsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/eqams')}>{t('eqams')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('eqamsReadinessHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('title')}>{row.title}</Descriptions.Item>
              <Descriptions.Item label={t('startedOn')}>{row.started_on}</Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {row.session_name || row.session_code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('frameworkCode')}>
                {row.framework_code
                  ? `${row.framework_code}${row.version_label ? ` / ${row.version_label}` : ''}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          {canManage && row?.status === 'draft' ? (
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" loading={setStatus.isPending} onClick={() => setStatus.mutate('in_progress')}>
                {t('startEqamsCycle')}
              </Button>
              <Button danger loading={setStatus.isPending} onClick={() => setStatus.mutate('cancelled')}>
                {t('cancel')}
              </Button>
            </Space>
          ) : null}
          {canManage && row?.status === 'in_progress' ? (
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" loading={setStatus.isPending} onClick={() => setStatus.mutate('closed')}>
                {t('closeEqamsCycle')}
              </Button>
              <Button danger loading={setStatus.isPending} onClick={() => setStatus.mutate('cancelled')}>
                {t('cancel')}
              </Button>
            </Space>
          ) : null}
        </Card>

        {(row?.domains || []).map((domain) => {
          const domainItems = (row?.items || []).filter((i) => i.domain_id === domain.id);
          return (
            <Card key={domain.id} size="small" title={`${domain.domain_code} — ${domain.title}`}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={domainItems}
                columns={[
                  { title: t('itemCode'), dataIndex: 'item_code', width: 90 },
                  { title: t('title'), dataIndex: 'title' },
                  {
                    title: t('readinessLevel'),
                    dataIndex: 'readiness_level',
                    width: 180,
                    render: (v: string) => <Tag>{v}</Tag>,
                  },
                  {
                    title: t('selfNotes'),
                    dataIndex: 'self_notes',
                    render: (v: string | null | undefined) => v || '—',
                  },
                ]}
                expandable={{
                  expandedRowRender: (item: Item) => {
                    const itemEvidence = (row?.evidence || []).filter((e) => e.item_id === item.id);
                    return (
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        {canManage && editable ? (
                          <Form
                            layout="inline"
                            initialValues={{
                              readiness_level: item.readiness_level,
                              self_notes: item.self_notes || undefined,
                            }}
                            onFinish={(values) =>
                              updateItem.mutate({
                                itemId: item.id,
                                readiness_level: values.readiness_level,
                                self_notes: values.self_notes,
                              })
                            }
                          >
                            <Form.Item name="readiness_level" label={t('readinessLevel')} rules={[{ required: true }]}>
                              <Select style={{ minWidth: 160 }} options={readinessOptions} />
                            </Form.Item>
                            <Form.Item name="self_notes" label={t('selfNotes')}>
                              <Input style={{ width: 220 }} maxLength={2000} />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={updateItem.isPending}>
                              {t('save')}
                            </Button>
                          </Form>
                        ) : null}
                        <Table
                          rowKey="id"
                          size="small"
                          pagination={false}
                          dataSource={itemEvidence}
                          columns={[
                            { title: t('evidenceType'), dataIndex: 'evidence_type', width: 110 },
                            { title: t('title'), dataIndex: 'title' },
                            {
                              title: t('reference'),
                              render: (_: unknown, e: Evidence) => e.reference_text || e.url || '—',
                            },
                          ]}
                        />
                        {canManage && editable ? (
                          <Form
                            layout="inline"
                            onFinish={(values) =>
                              addEvidence.mutate({
                                item_id: item.id,
                                evidence_type: values.evidence_type,
                                title: values.title,
                                reference_text: values.reference_text,
                                url: values.url,
                              })
                            }
                          >
                            <Form.Item name="evidence_type" label={t('evidenceType')} rules={[{ required: true }]}>
                              <Select
                                style={{ minWidth: 120 }}
                                options={[
                                  { value: 'note', label: t('evidenceNote') },
                                  { value: 'url', label: t('evidenceUrl') },
                                  { value: 'internal_ref', label: t('evidenceInternalRef') },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
                              <Input style={{ width: 160 }} maxLength={200} />
                            </Form.Item>
                            <Form.Item name="reference_text" label={t('reference')}>
                              <Input style={{ width: 180 }} maxLength={2000} />
                            </Form.Item>
                            <Form.Item name="url" label={t('url')}>
                              <Input style={{ width: 180 }} maxLength={1000} />
                            </Form.Item>
                            <Button htmlType="submit" loading={addEvidence.isPending}>
                              {t('addEvidence')}
                            </Button>
                          </Form>
                        ) : null}
                      </Space>
                    );
                  },
                }}
              />
            </Card>
          );
        })}

        <Card size="small" title={t('eqamsSip')}>
          {(row?.sips || []).map((sip) => (
            <Card
              key={sip.id}
              type="inner"
              size="small"
              style={{ marginBottom: 12 }}
              title={`${sip.sip_no} — ${sip.title}`}
              extra={<Tag>{sip.status}</Tag>}
            >
              <p>{sip.summary}</p>
              {canManage && sip.status !== 'closed' ? (
                <Space style={{ marginBottom: 12 }}>
                  {sip.status === 'draft' ? (
                    <Button
                      size="small"
                      onClick={() =>
                        sipStatus.mutate({ sipId: sip.id, status: 'active', record_version: sip.record_version })
                      }
                    >
                      {t('activateSip')}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    onClick={() =>
                      sipStatus.mutate({ sipId: sip.id, status: 'closed', record_version: sip.record_version })
                    }
                  >
                    {t('closeSip')}
                  </Button>
                </Space>
              ) : null}
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={sip.actions || []}
                columns={[
                  { title: t('action'), dataIndex: 'action_text' },
                  { title: t('dueOn'), dataIndex: 'due_on', width: 120, render: (v: string) => v || '—' },
                  {
                    title: t('status'),
                    dataIndex: 'status',
                    width: 160,
                    render: (v: string, action: SipAction) =>
                      canManage && sip.status !== 'closed' ? (
                        <Select
                          size="small"
                          value={v}
                          style={{ width: 120 }}
                          onChange={(status) => actionStatus.mutate({ actionId: action.id, status })}
                          options={[
                            { value: 'open', label: t('statusOpen') },
                            { value: 'done', label: t('statusDone') },
                            { value: 'cancelled', label: t('statusCancelled') },
                          ]}
                        />
                      ) : (
                        <Tag>{v}</Tag>
                      ),
                  },
                ]}
              />
              {canManage && sip.status !== 'closed' ? (
                <Form
                  layout="inline"
                  style={{ marginTop: 12 }}
                  onFinish={(values) =>
                    addAction.mutate({
                      sip_id: sip.id,
                      action_text: values.action_text,
                      due_on: values.due_on,
                      linked_item_id: values.linked_item_id,
                    })
                  }
                >
                  <Form.Item name="action_text" label={t('action')} rules={[{ required: true }]}>
                    <Input style={{ width: 260 }} maxLength={500} />
                  </Form.Item>
                  <Form.Item name="due_on" label={t('dueOn')}>
                    <DatePicker />
                  </Form.Item>
                  <Form.Item name="linked_item_id" label={t('linkedItemOptional')}>
                    <Select
                      allowClear
                      style={{ minWidth: 160 }}
                      options={(row?.items || []).map((i) => ({ value: i.id, label: i.item_code }))}
                    />
                  </Form.Item>
                  <Button htmlType="submit" loading={addAction.isPending}>
                    {t('addAction')}
                  </Button>
                </Form>
              ) : null}
            </Card>
          ))}
          {canManage && row && row.status !== 'cancelled' ? (
            <Form layout="vertical" style={{ maxWidth: 520 }} onFinish={(values) => createSip.mutate(values)}>
              <Form.Item name="title" label={t('sipTitle')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="summary" label={t('sipSummary')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={createSip.isPending}>
                {t('createSip')}
              </Button>
            </Form>
          ) : null}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
